import { useCreativesConfig } from '@ad-lucy/context/creativesConfig';
import { getModuleLogger } from '@ad-lucy/context/LoggerService';
import { useFetch } from '@ad-lucy/hooks/fetch/useFetch';
import { LocatorService } from '@ad-lucy/utils/location';
import { createContext, type ReactElement, useEffect, useMemo, useState } from 'react';

import type {
  ContentConfig,
  ContentContextValue,
  ContentDataProviderProps,
  RawContentConfig,
} from './types';
import { useCombinedGridMap } from './useCombinedGridMap';
import { transformContent } from './utils/transformContent';

export const ContentContext = createContext<ContentContextValue>({} as ContentContextValue);
const logger = getModuleLogger('ContentDataProvider');

export function ContentDataProvider({
  children,
  url,
  locationsUrl,
  locationsLightUrl,
  load = true,
}: Readonly<ContentDataProviderProps>): ReactElement {
  const { components } = useCreativesConfig();
  const combinedGridMap = useCombinedGridMap();

  const [rawContent, setRawContent] = useState<RawContentConfig>();
  const [content, setContent] = useState<ContentConfig>();

  const { get, loading, error } = useFetch<RawContentConfig>();

  useEffect(() => {
    if (load) {
      const fetchContent = async (): Promise<RawContentConfig | undefined> => {
        // Locations only need to be fetched if url is not provided
        const location = url
          ? undefined
          : await LocatorService(locationsUrl, locationsLightUrl).getLocationFromQueryParams();
        return await get(url ?? `content/content-${location?.id}.json`);
      };

      fetchContent().then(setRawContent).catch(logger.error);
    }
  }, [get, load, locationsLightUrl, locationsUrl, url]);

  useEffect(() => {
    if (rawContent) {
      setContent(transformContent(components, combinedGridMap, rawContent));
    }
  }, [components, combinedGridMap, rawContent]);

  const contextData = useMemo(
    () => ({
      data: {
        loading,
        error: error?.message,
        data: content,
      },
    }),
    [loading, error?.message, content],
  );

  return <ContentContext.Provider value={contextData}>{children}</ContentContext.Provider>;
}
